import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Truck, BadgeIndianRupee, ArrowRight, ShoppingCart, Search } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const HERO_IMG = "https://images.unsplash.com/photo-1658298775754-5839ffd434cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVscyUyMHJvb2Z8ZW58MHx8fHwxNzg3MTA4NDAyfDA&ixlib=rb-4.1.0&q=85";
const SECONDARY_IMG = "https://images.unsplash.com/photo-1740805276608-ef60e2e468ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHN0cmVldCUyMGxpZ2h0fGVufDB8fHx8MTc4NzEwODQ1Mnww&ixlib=rb-4.1.0&q=85";

function ProductCard({ product, index }) {
  const { addItem } = useCart();
  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.06 }}
      className="bg-white border border-border rounded-md overflow-hidden transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg flex flex-col"
    >
      <div className="aspect-[4/3] overflow-hidden bg-muted">
        <img src={product.image} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-secondary mb-1">{product.category}</p>
        <h3 className="font-heading font-medium text-lg leading-snug truncate">{product.name}</h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2 flex-1">{product.description}</p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <span data-testid={`product-price-${product.id}`} className="font-heading font-bold text-lg text-primary">{inr(product.price)}</span>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={() => { addItem(product); toast.success(`${product.name} added to cart`); }}
            className="flex items-center gap-2 rounded-full bg-primary text-white text-sm font-medium px-4 py-2 hover:bg-primary/90 active:scale-95 transition-[background-color,transform] duration-200 focus:ring-2 focus:ring-primary"
          >
            <ShoppingCart className="w-4 h-4" /> Add to Cart
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => toast.error("Could not load products"));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(products.map((p) => p.category))], [products]);
  const filtered = useMemo(() => {
    let list = category === "All" ? products : products.filter((p) => p.category === category);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
    return list;
  }, [products, category, query]);

  return (
    <div data-testid="home-page" className="min-h-screen">
      <Header />

      <section className="max-w-7xl mx-auto px-6 pt-10 sm:pt-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8 md:row-span-2 relative rounded-md overflow-hidden border border-border min-h-[420px] fade-up">
            <img src={HERO_IMG} alt="Solar panels on a rooftop" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 h-full flex flex-col justify-end p-8 sm:p-12">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">Jehovah Nissi Solar Agency &middot; Trichy</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-2xl">
                Power your home with the sun
              </h1>
              <p className="text-slate-200 text-base leading-relaxed mt-4 max-w-xl">
                Solar panels, inverters, batteries, water heaters and lights — genuine products with installation support across Thiruchappali.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href="#shop" data-testid="hero-shop-cta" className="inline-flex items-center gap-2 rounded-full bg-primary text-white font-medium px-6 py-3 hover:bg-primary/90 active:scale-95 transition-[background-color,transform] duration-200">
                  Shop Solar Products <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#contact" data-testid="hero-contact-cta" className="inline-flex items-center gap-2 rounded-full border border-white/60 text-white font-medium px-6 py-3 hover:bg-white/10 transition-colors duration-200">
                  Talk to Us
                </a>
              </div>
            </div>
          </div>
          <div className="md:col-span-4 bg-white border border-border rounded-md p-8 flex flex-col justify-center gap-6 fade-up">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-6 h-6 text-secondary shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="font-heading font-semibold">Up to 25-Year Warranty</p>
                <p className="text-sm text-muted-foreground">Genuine panels with manufacturer warranty and service support.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Truck className="w-6 h-6 text-secondary shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="font-heading font-semibold">Delivery &amp; Installation</p>
                <p className="text-sm text-muted-foreground">Doorstep delivery with expert installation across Trichy district.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <BadgeIndianRupee className="w-6 h-6 text-secondary shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="font-heading font-semibold">Subsidy Guidance</p>
                <p className="text-sm text-muted-foreground">Help with government solar subsidy paperwork for eligible homes.</p>
              </div>
            </div>
          </div>
          <div className="md:col-span-4 relative rounded-md overflow-hidden border border-border min-h-[220px] fade-up group">
            <img src={SECONDARY_IMG} alt="Solar street light" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 h-full flex flex-col justify-end p-6">
              <p className="font-heading font-semibold text-white text-lg">Street Lights, Garden Lights &amp; More</p>
              <a href="#shop" data-testid="hero-lights-link" className="text-orange-300 text-sm font-medium mt-1 inline-flex items-center gap-1 hover:gap-2 transition-all duration-200">
                Explore lights <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="shop" data-testid="shop-section" className="max-w-7xl mx-auto px-6 pt-20 sm:pt-28">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">Our Catalog</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">130 solar products, ready to order</h2>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={2} />
            <input
              data-testid="product-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products... e.g. battery, pump"
              className="w-full rounded-full border border-border bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-[box-shadow,border-color] duration-200"
            />
          </div>
        </div>
        <div data-testid="category-filters" className="flex flex-wrap gap-2 mb-8">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors duration-200 ${category === c ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary hover:text-primary"}`}
              >
                {c}
              </button>
            ))}
        </div>
        {query.trim() && (
          <p data-testid="search-results-count" className="text-sm text-muted-foreground mb-6">
            {filtered.length} result{filtered.length === 1 ? "" : "s"} for &ldquo;{query.trim()}&rdquo;
          </p>
        )}
        <div data-testid="product-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filtered.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
        {products.length > 0 && filtered.length === 0 && (
          <div data-testid="search-no-results" className="bg-white border border-border rounded-md p-12 text-center">
            <p className="font-heading font-medium text-lg">No products found for &ldquo;{query.trim()}&rdquo;</p>
            <p className="text-sm text-muted-foreground mt-1">Try a different name or category, or call us at +91 63741 35100.</p>
            <button data-testid="clear-search-button" onClick={() => setQuery("")} className="mt-4 rounded-full bg-primary text-white text-sm font-medium px-5 py-2 hover:bg-primary/90 active:scale-95 transition-[background-color,transform] duration-200">
              Clear Search
            </button>
          </div>
        )}
        {products.length === 0 && <p data-testid="products-loading" className="text-sm text-muted-foreground mt-8">Loading products...</p>}
      </section>

      <section id="why-solar" data-testid="why-solar-section" className="max-w-7xl mx-auto px-6 pt-24">
        <div className="bg-secondary rounded-md p-8 sm:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-green-200 mb-3">Why Solar, Why Now</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
              A typical Trichy home saves up to ₹3,000 every month on electricity
            </h2>
            <p className="text-green-100 text-base leading-relaxed mt-4 max-w-2xl">
              With 300+ sunny days a year in Tamil Nadu, solar pays for itself in 3-4 years and keeps saving for decades. Order online and pay on delivery — we handle delivery, installation and after-sales service.
            </p>
          </div>
          <div className="md:col-span-4">
            <a href="#shop" data-testid="why-solar-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-secondary font-medium px-6 py-3 hover:bg-green-50 active:scale-95 transition-[background-color,transform] duration-200">
              Start Shopping <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
